import { format } from 'date-fns';
import { TimesheetEntryPayloadEntry } from '@sdk/timesheets/api/apiTypes';
import { TimesheetEntry, TimesheetEntryType } from '@sdk/timesheets/types/TimesheetEntry';
import { AllBreakTypes } from '@sdk/settings/types';
import { AutomatedBreaksSettingsUI } from '@sdk/automatedBreaks/types/AutomatedBreaksSettingsUI';

import {
  BreakEntryWithDates,
  FieldValues,
  TimeClockEntryWithDates,
} from '@web/timesheets/CreateTimesheetModal/createTimesheetModal.types';
import { getClockInClockOutDates } from '@web/timesheets/CreateTimesheetModal/createTimesheetModal.utils';
import maybeExtendBreakWithAutomatedBreaksDetails from '@web/timesheets/CreateTimesheetModal/utils/maybeExtendBreakWithAutomatedBreaksDetails';
import getAutomatedBreaksSettingsForEntry from '@web/timesheets/CreateTimesheetModal/utils/getAutomatedBreaksSettingsForEntry';

export const mapTimesheetFormFieldsToBulkPayload = (
  fieldValues: FieldValues
): TimesheetEntryPayloadEntry[] => {
  const { date, employee, location, position, timeClock } = fieldValues;
  const { id: employeeId } = employee[0];
  const { id: locationId } = location[0];
  const { id: positionId } = position[0];

  const getPayloadRecord = (
    dateString: string,
    time: string,
    type: TimesheetEntryType,
    breakType = AllBreakTypes.standard
  ) => ({
    timestamp: `${dateString}T${time}`,
    baseSequenceId: null,
    sequenceId: null,
    positionId,
    locationId,
    user: {
      id: employeeId,
    },
    type,
    automatedBreakType: [AllBreakTypes.meal, AllBreakTypes.rest].includes(breakType)
      ? breakType
      : undefined,
  });

  const payloadEntries = [];

  timeClock.forEach(({ clockIn: clockInTime, clockOut: clockOutTime, breaks, deleted }) => {
    const { clockIn, clockOut } = getClockInClockOutDates(date, clockInTime, clockOutTime);

    if (clockInTime) {
      const clockInDateString = format(clockIn, 'yyyy-MM-dd');
      payloadEntries.push({
        ...getPayloadRecord(clockInDateString, clockInTime, TimesheetEntryType.clockIn),
        deleted,
      });
    }

    breaks.forEach(({ start: startTime, end: endTime, type, deleted: isBreakDeleted }) => {
      const { clockIn: start, clockOut: end } = getClockInClockOutDates(
        clockIn,
        startTime,
        endTime,
        { isDateWithTime: true }
      );

      if (startTime) {
        const startDateString = format(start, 'yyyy-MM-dd');
        payloadEntries.push({
          ...getPayloadRecord(startDateString, startTime, TimesheetEntryType.breakStart, type),
          deleted: isBreakDeleted,
        });
      }

      if (endTime) {
        const endDateString = format(end, 'yyyy-MM-dd');
        payloadEntries.push({
          ...getPayloadRecord(endDateString, endTime, TimesheetEntryType.breakEnd, type),
          deleted: isBreakDeleted,
        });
      }
    });

    if (clockOutTime) {
      const clockOutDateString = format(clockOut, 'yyyy-MM-dd');
      payloadEntries.push({
        ...getPayloadRecord(clockOutDateString, clockOutTime, TimesheetEntryType.clockOut),
        deleted,
      });
    }
  });

  return payloadEntries;
};

export const getBreakTimesheetEntry = (
  breakEntry: BreakEntryWithDates,
  type: TimesheetEntryType,
  automatedBreaks: AutomatedBreaksSettingsUI
) => {
  const { start, startEntry, end, endEntry } = breakEntry;

  const timestamp = type === TimesheetEntryType.breakStart ? start : end;
  const timesheetEntry = type === TimesheetEntryType.breakStart ? startEntry : endEntry;

  const entry: TimesheetEntry = {
    id: timesheetEntry?.id || null,
    baseSequenceId: timesheetEntry?.baseSequenceId || null,
    sequenceId: timesheetEntry?.sequenceId || null,
    timestamp: timestamp,
    timestampLocal: timestamp,
    replacesAttestedBreakNumber: breakEntry.replacesAttestedBreakNumber,
    type,
  };
  return maybeExtendBreakWithAutomatedBreaksDetails(entry, breakEntry.type, automatedBreaks);
};

/**
 * This mapper is used for DurationRow calculations. We want to be able to map form values from single TimeClockEntry,
 * which is one full section from clock in to clock out (including breaks), to timesheet entry, which is then used
 * for both summary display (example: 3 missed rest breaks) as well as duration (example: 3h);
 *
 * @param timeClockEntry
 * @param automatedBreaks
 * @returns
 */
export const mapTimeClockEntryWithDateToTimesheetEntry = (
  timeClockEntry: TimeClockEntryWithDates,
  automatedBreaks: AutomatedBreaksSettingsUI
): TimesheetEntry[] => {
  const entries = [];

  if (timeClockEntry.deleted) {
    return entries;
  }

  const { clockIn, clockInEntry, clockOut, clockOutEntry } = timeClockEntry;
  if (clockIn) {
    entries.push({
      id: clockInEntry?.id || null,
      baseSequenceId: clockInEntry?.baseSequenceId,
      timestamp: clockIn,
      timestampLocal: clockIn,
      type: TimesheetEntryType.clockIn,
      ...getAutomatedBreaksSettingsForEntry(automatedBreaks),
    });
  }

  // Todo if clock in is in progress, has already one break in, shouldn't we still do breaks?.
  if (clockOut) {
    timeClockEntry.breaks.forEach((breakEntry) => {
      if (!breakEntry.deleted) {
        if (breakEntry.start) {
          entries.push(
            getBreakTimesheetEntry(breakEntry, TimesheetEntryType.breakStart, automatedBreaks)
          );
        }

        if (breakEntry.end) {
          entries.push(
            getBreakTimesheetEntry(breakEntry, TimesheetEntryType.breakEnd, automatedBreaks)
          );
        }
      }
    });

    entries.push({
      id: clockOutEntry?.id || null,
      baseSequenceId: clockOutEntry?.baseSequenceId,
      timestamp: clockOut,
      timestampLocal: clockOut,
      type: TimesheetEntryType.clockOut,
      ...getAutomatedBreaksSettingsForEntry(automatedBreaks),
    });
  }

  return entries;
};
